// =============================================================================
// analytics-top - ranking for the Toppers page (best days / weeks / months)
// =============================================================================
// Built on fn_admin_analytics_timeseries rows: one row per (bucket,
// experience), summed here per bucket and ranked. Same numbers as the Analyse
// page (viewTimeseriesRow): omzet = captured cash ex BTW, tickets include the
// voucher tickets sold and leave out redeemed vouchers.
// =============================================================================
import { viewTimeseriesRow, type TimeseriesRow } from './analytics';
import { addDaysVenue, venueWeekLabel, venueWeekStart } from './dates';

export type TopBucket = 'day' | 'week' | 'month';
export type TopMetric = 'revenue_ex_vat_cents' | 'guests';
export type TopRange = 'this_year' | 'last_year' | 'last_12' | 'all';

export interface ExperienceShare {
  name: string;
  revenue_ex_vat_cents: number;
  guests: number;
}

export interface PeriodTotal {
  period: string; // bucket start, 'YYYY-MM-DD'
  revenue_ex_vat_cents: number;
  guests: number;
  bookings: number;
  byExperience: ExperienceShare[];
}

// The booking system went live in 2026; the imported CCV counter taps go back
// to 2025, so "Alles" starts there.
export const ALL_FROM = '2025-01-01';

export function topRangeFor(range: TopRange, bucket: TopBucket, today: string): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  let from: string;
  let to = today;
  if (range === 'this_year') from = `${y}-01-01`;
  else if (range === 'last_year') { from = `${y - 1}-01-01`; to = `${y - 1}-12-31`; }
  else if (range === 'last_12') from = addDaysVenue(today, -364);
  else from = ALL_FROM;
  // Whole buckets only: a week or month cut off by the range start would rank
  // with a fraction of its days.
  if (bucket === 'week') from = venueWeekStart(from);
  if (bucket === 'month') from = `${from.slice(0, 7)}-01`;
  return { from, to };
}

// Sum the per-experience rows of each bucket. Omzet is rounded once per bucket
// from the unrounded row amounts: adding rows that were each rounded to a cent
// put days with the same takings a cent apart depending on how many
// experiences they were split over.
export function periodTotals(rows: TimeseriesRow[], showRedeemed = false): PeriodTotal[] {
  const m = new Map<string, PeriodTotal>();
  const exact = new Map<string, number | null>(); // null: a row without the exact key
  for (const r of rows) {
    const v = viewTimeseriesRow(r, showRedeemed);
    const key = r.period.slice(0, 10);
    let t = m.get(key);
    if (!t) {
      t = { period: key, revenue_ex_vat_cents: 0, guests: 0, bookings: 0, byExperience: [] };
      m.set(key, t);
      exact.set(key, 0);
    }
    t.revenue_ex_vat_cents += v.revenue_ex_vat_cents;
    const sum = exact.get(key);
    exact.set(key, sum == null || r.revenue_ex_vat_exact == null ? null : sum + Number(r.revenue_ex_vat_exact));
    t.guests += v.guests;
    t.bookings += v.bookings;
    let e = t.byExperience.find((x) => x.name === r.experience_name);
    if (!e) {
      e = { name: r.experience_name, revenue_ex_vat_cents: 0, guests: 0 };
      t.byExperience.push(e);
    }
    e.revenue_ex_vat_cents += v.revenue_ex_vat_cents;
    e.guests += v.guests;
  }
  for (const t of m.values()) {
    const sum = exact.get(t.period);
    if (sum != null) t.revenue_ex_vat_cents = Math.round(sum);
  }
  return [...m.values()];
}

// Highest first; a tie goes to the earlier period (it got there first).
// Empty buckets (closed days) never rank.
export function rankPeriods(totals: PeriodTotal[], metric: TopMetric, n: number): PeriodTotal[] {
  return totals
    .filter((t) => t[metric] > 0)
    .sort((a, b) => b[metric] - a[metric] || a.period.localeCompare(b.period))
    .slice(0, n);
}

export interface TopSummary {
  count: number;   // buckets with a non-zero value
  average: number; // over those buckets
  median: number;
}

export function summarize(totals: PeriodTotal[], metric: TopMetric): TopSummary {
  const vals = totals.map((t) => t[metric]).filter((v) => v > 0).sort((a, b) => a - b);
  if (vals.length === 0) return { count: 0, average: 0, median: 0 };
  const mid = Math.floor(vals.length / 2);
  const median = vals.length % 2 ? vals[mid]! : Math.round((vals[mid - 1]! + vals[mid]!) / 2);
  return {
    count: vals.length,
    average: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
    median,
  };
}

const MONTHS_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

// ISO 8601 week number of a date string (weeks start Monday; week 1 holds
// the first Thursday of the year).
export function isoWeek(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d!));
  const dow = (t.getUTCDay() + 6) % 7; // Monday = 0
  t.setUTCDate(t.getUTCDate() - dow + 3); // Thursday of this week
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  return Math.ceil(((t.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

export function periodTitle(bucket: TopBucket, period: string, formatDay: (d: string) => string): string {
  if (bucket === 'day') return formatDay(period);
  if (bucket === 'week') return `Week ${isoWeek(period)} · ${venueWeekLabel(period)}`;
  return `${MONTHS_NL[Number(period.slice(5, 7)) - 1]} ${period.slice(0, 4)}`;
}

// Is today inside this bucket (a running period, still filling up)?
export function isRunning(bucket: TopBucket, period: string, today: string): boolean {
  if (bucket === 'day') return period === today;
  if (bucket === 'week') return venueWeekStart(today) === period;
  return today.slice(0, 7) === period.slice(0, 7);
}
